import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  Menu,
  TableRow,
  TableCell,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";
import { DeleteIconTable, DownArrowIcon } from "../../../assets/Icon/ArrowIcon";
import { useDispatch } from "react-redux";
import {
  createRules,
  deleteRule,
  getCategories,
  getRuleByID,
  getRulesList,
} from "../../../stores/clients";
import {
  ModalTop,
  ModalBottom,
  CrossIcon,
} from "../../../assets/Icon/ModalIcon";
import {
  CloseButton,
  CloseButtonWrapper,
  ModalWrapper,
} from "../../style/modalStyle";
import CustomTable from "../../common-components/CustomTable";
import { EditIcon, TrashIconForPopup } from "../../../assets/Icon/headerCicon";
import CommonPagination from "../../common-components/CommonPagination";
import { setToast } from "../../../stores/toast/toastslice";

const BookkeepingRules = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [pageNo, setPageNo] = useState(1);
  const [limit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [data, setData] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCategory, setOpenCategory] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryID, setCategoryID] = useState(0);
  const [categoryValue, setCategoryValue] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [ruleId, setRuleId] = useState(0);
  const [isError, setIsError] = useState({
    value: false,
    condition: false,
    type: false,
    category_id: false,
  });
  const [listLoading, setListLoading] = useState(true);
  const [createLoader, setCreateLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const dispatch = useDispatch();

  const [ruleData, setRuleData] = useState({
    rule_id: 0,
    type: "", // description or amount
    condition: "",
    value: "",
    category_id: 0,
  });

  useEffect(() => {
    const categoriesData = async () => {
      try {
        await dispatch<any>(getCategories(1)).then((res: any) => {
          setCategories(res?.payload?.data);
        });
      } catch (error: any) {
        console.log(error.response?.data?.error || error.message);
      }
    };

    categoriesData();
    fetchRules();
  }, []);

  useEffect(() => {
    fetchRules();
  }, [pageNo]);

  const fetchRules = async () => {
    await dispatch<any>(getRulesList({ page: pageNo, limit }))
      .then((res: any) => {
        setData(res?.payload?.data);
        setTotalRecords(res?.payload?.total_records);
        console.log(res?.payload);
      })
      .finally(() => setListLoading(false));
  };

  const handleChange = (field: string, value: any) => {
    setRuleData((prev) => ({ ...prev, [field]: value }));
    setIsError((prev) => ({ ...prev, [field]: false }));
  };

  const getConditionOptions = () => {
    if (ruleData.type === "description") {
      return ["contains", "starts with", "ends with", "equals"];
    } else if (ruleData.type === "amount") {
      return ["<", ">", "=", "<=", ">="];
    }
    return [];
  };

  const handleDropdownSelect = (field: string, value: any) => {
    if (field === "type") {
      // When type changes, reset dependent values but don't clear errors unintentionally
      setRuleData((prev) => ({
        ...prev,
        type: value,
        condition: "",
        value: "",
        category_id: 0,
      }));

      // Clear only the type error (if any)
      setIsError((prev) => ({
        ...prev,
        type: false,
      }));

      setCategoryValue("");
      setOpenDropdown(null);
    } else {
      // For other dropdowns, use normal handleChange
      handleChange(field, value);
      setOpenDropdown(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const reset = () => {
    setRuleData({
      rule_id: 0,
      type: "",
      condition: "",
      category_id: 0,
      value: "",
    });
    setCategoryValue("");
    setOpenDropdown(null);
    setOpenModal(false);
    setIsEdit(false);
    setIsError({
      value: false,
      type: false,
      condition: false,
      category_id: false,
    });
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  return (
    <div>
      {/* Back Button */}
      <Button
        sx={{
          mb: 0.5,
          color: "#6C6F71",
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
          fontWeight: 600,
          gap: 0.5,
          ml: -2,
        }}
        onClick={() => navigate("/tools/bookkeeper/dashboard")}
      >
        <ArrowBackIosNewIcon /> Back
      </Button>

      {/* Header */}

      <div
        style={{
          background: "#fff",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px",
          borderRadius: "2px",
          marginTop: "16px",
        }}
      >
        <div
          style={{
            fontSize: isMobile ? "18px" : "22px",
            fontWeight: "500",
            color: "#060110",
          }}
        >
          Rules
        </div>
        <Button
          onClick={() => setOpenModal(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "linear-gradient(to right, #2977CC, #1D538F)",
            fontWeight: 600,
            fontSize: "14px",
            color: "#ffffff",
            padding: "10px 40px",
          }}
        >
          Add Rule
        </Button>
      </div>

      {/* Add Rule Modal */}
      <Modal open={openModal} onClose={reset}>
        <ModalWrapper
          sx={{
            width: isMobile ? "98%" : "100%",
            maxWidth: "837px",
            height: "fit-content",
            backgroundImage: "none",
            position: "relative",
            borderRadius: "20px",
            overflow: "visible",
            outline: "none",
          }}
        >
          {/* Decorations wrapper with hidden overflow */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              borderRadius: "20px",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            {/* Top right decoration */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
              }}
            >
              <ModalTop />
            </div>

            {/* Bottom left decoration */}
            <div
              style={{
                position: "absolute",
                bottom: -6,
                left: 0,
              }}
            >
              <ModalBottom />
            </div>
          </div>
          <CloseButtonWrapper
            sx={{ zIndex: 10, position: "relative", marginTop: 0 }}
          >
            <CloseButton
              onClick={() => {
                reset();
                setOpenDropdown(null);
              }}
            >
              <CrossIcon />
            </CloseButton>
          </CloseButtonWrapper>
          <Box
            sx={{
              width: "100%",
              px: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 3, color: "#060110" }}
            >
              {isEdit ? "Edit" : "Add"} Bookkeeper Rule
            </Typography>

            {/* Inline Form */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 10,
              }}
            >
              {/* TYPE DROPDOWN */}
              <div style={{ position: "relative" }}>
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
                  Type
                </Typography>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: isError.type ? "red" : "#A7C7EA",
                    borderRadius: "8px",
                    height: "50px",
                    color: ruleData.type?.length ? "#000" : "#97999B",
                    fontSize: "16px",
                    fontWeight: 500,
                    width: "180px",
                    justifyContent: "space-between",
                    textTransform: "none",
                  }}
                  onClick={() =>
                    setOpenDropdown(openDropdown === "type" ? null : "type")
                  }
                >
                  {ruleData.type
                    ? ruleData.type === "description"
                      ? "Description"
                      : "Amount"
                    : "Select Type"}
                  <DownArrowIcon />
                </Button>
                {openDropdown === "type" && (
                  <Box
                    sx={{
                      border: "#A7C7EA",
                      position: "absolute",
                      top: "85px",
                      bgcolor: "white",
                      borderRadius: "8px",
                      width: 180,
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                      zIndex: 1000,
                    }}
                  >
                    {["description", "amount"].map((opt) => (
                      <MenuItem
                        key={opt}
                        onClick={() => handleDropdownSelect("type", opt)}
                        sx={{
                          "&:hover": { backgroundColor: "#A7C7EA66" },
                          fontSize: "16px",
                        }}
                      >
                        {opt === "description" ? "Description" : "Amount"}
                      </MenuItem>
                    ))}
                  </Box>
                )}
                {/* Error placeholder */}
                <div
                  style={{
                    minHeight: "22px",
                    marginTop: "4px",
                  }}
                >
                  {isError?.type && (
                    <span
                      style={{
                        color: "red",
                        fontSize: "14px",
                        fontWeight: 400,
                      }}
                    >
                      Type is required!
                    </span>
                  )}
                </div>
              </div>

              {/* CONDITION DROPDOWN */}
              <div style={{ position: "relative" }}>
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
                  Condition
                </Typography>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: isError.condition ? "red" : "#A7C7EA",
                    borderRadius: "8px",
                    height: "50px",
                    color: ruleData.condition?.length ? "#000" : "#97999B",
                    fontSize: "16px",
                    fontWeight: 500,
                    width: "160px",
                    justifyContent: "space-between",
                    textTransform: "none",
                  }}
                  // disabled={!ruleData.type}
                  onClick={() => {
                    if (!ruleData?.type) return;
                    setOpenDropdown(
                      openDropdown === "condition" ? null : "condition"
                    );
                  }}
                >
                  {ruleData.condition || "Select"}
                  <DownArrowIcon />
                </Button>
                {openDropdown === "condition" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "85px",
                      bgcolor: "white",
                      borderRadius: "8px",
                      width: 160,
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                      zIndex: 100,
                    }}
                  >
                    {getConditionOptions().map((opt) => (
                      <MenuItem
                        key={opt}
                        onClick={() => handleDropdownSelect("condition", opt)}
                        sx={{
                          "&:hover": { backgroundColor: "#A7C7EA66" },
                          fontSize: "16px",
                        }}
                      >
                        {opt}
                      </MenuItem>
                    ))}
                  </Box>
                )}
                {/* Error placeholder */}
                <div
                  style={{
                    minHeight: "22px",
                    marginTop: "4px",
                  }}
                >
                  {isError?.condition && (
                    <span
                      style={{
                        color: "red",
                        fontSize: "14px",
                        fontWeight: 400,
                      }}
                    >
                      Condition is required!
                    </span>
                  )}
                </div>
              </div>

              {/* VALUE INPUT */}
              <div>
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
                  Value
                </Typography>
                <TextField
                  value={ruleData.value}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (ruleData.type === "amount") {
                      // Allow only digits, $, and optionally decimal
                      val = val.replace(/[^0-9.,]/g, "");
                    }
                    handleChange("value", val);
                  }}
                  placeholder={
                    ruleData.type === "amount"
                      ? "Enter amount"
                      : "Enter value"
                  }
                  sx={{
                    minWidth: 120,
                    "& .MuiInputBase-root": {
                      height: "50px",
                      width: 200,
                      border: isError?.value
                        ? "1px solid #ff0000ff"
                        : "1px solid #A7C7EA",
                      borderRadius: "8px",
                      paddingX: 0.5,
                      fontWeight: 500,
                      color: "#000",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none", // hide MUI default outline
                    },
                    "& .Mui-focused .MuiInputBase-root": {
                      border: "1px solid #2977CC", // custom border color on focus
                    },
                  }}
                />
                {/* Error placeholder */}
                <div
                  style={{
                    minHeight: "22px",
                    marginTop: "4px",
                  }}
                >
                  {isError?.value && (
                    <span
                      style={{
                        color: "red",
                        fontSize: "14px",
                        fontWeight: 400,
                      }}
                    >
                      Value is required!
                    </span>
                  )}
                </div>
              </div>

              {/* Category Dropdown */}
              <div style={{ position: "relative" }}>
                {/* Red dot indicator when filter is applied */}
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
                  Category
                </Typography>
                <Button
                  style={{
                    border: isError?.category_id
                      ? "1px solid #ff0000ff"
                      : "1px solid #A7C7EA",
                    borderRadius: "8px",
                    height: "50px",
                    padding: 15,
                    color: categoryValue ? "#000" : "#97999B",
                    fontSize: "16px",
                    fontWeight: 500,
                    gap: 10,
                    width: "200px",
                    position: "relative",
                  }}
                  onClick={(event: any) => {
                    setAnchorEl(event.currentTarget);
                    setOpenCategory(!openCategory);
                  }}
                >
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    {" "}
                    {categoryValue?.length > 12
                      ? `${categoryValue?.slice(0, 12)}...`
                      : categoryValue || "Select Category"}
                    <DownArrowIcon />
                  </p>
                </Button>
                {openCategory && (
                  <div
                    style={{
                      position: "absolute",
                      marginTop: 10,
                      backgroundColor: "white",
                      borderRadius: "8px",
                      width: "480px",
                      right: isMobile ? "" : "0px",
                      zIndex: 1000,
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {openCategory && (
                      <Menu
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setOpenCategory(false)}
                        PaperProps={{
                          style: {
                            borderRadius: "8px",
                            marginTop: 10,
                            width: "400px",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                            height: categories?.length > 1 ? "150px" : "",
                          },
                        }}
                      >
                        {categories?.map((category: any, idx) => (
                          <MenuItem
                            key={idx}
                            onClick={() => {
                              console.log("Filter by year:", category);
                              setOpenCategory(false);
                              setCategoryValue(category?.name);
                              setCategoryID(category?.id);
                              handleChange("category_id", category?.id);
                            }}
                            sx={{
                              borderRadius: "6px",
                              "&:hover": {
                                backgroundColor: "#A7C7EA66", // light blue hover
                                color: "#1D538F",
                                borderRadius: 0,
                              },
                              color: "#6C6F71",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                paddingTop: 2,
                                paddingBottom: 2,
                              }}
                            >
                              {category?.name?.length > 40
                                ? `${category?.name?.slice(0, 40)}...`
                                : category?.name}
                            </div>
                          </MenuItem>
                        ))}
                      </Menu>
                    )}
                  </div>
                )}
                {/* Error placeholder */}
                <div
                  style={{
                    minHeight: "22px",
                    marginTop: "4px",
                  }}
                >
                  {isError?.category_id && (
                    <span
                      style={{
                        color: "red",
                        fontSize: "14px",
                        fontWeight: 400,
                      }}
                    >
                      Category is required!
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 25,
                gap: 20,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  reset();
                  setOpenDropdown(null);
                }}
                sx={{
                  borderColor: "#A7C7EA",
                  color: "#1D538F",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                sx={{
                  background: createLoader
                    ? "#ccc"
                    : "linear-gradient(to right, #2977CC, #1D538F)",
                  fontWeight: 600,
                  cursor: createLoader ? "not-allowed" : "pointer",
                  px: 5,
                  py: 1,
                }}
                onClick={async () => {
                  console.log("Rule Data:", ruleData);
                  const { value, type, category_id, condition } = ruleData;

                  // Validation check
                  const newErrors = {
                    value: !value,
                    type: !type,
                    category_id: !category_id,
                    condition: !condition,
                  };

                  setIsError(newErrors);

                  // Stop if any field is invalid
                  if (Object.values(newErrors).some(Boolean)) return;

                  setCreateLoader(true);
                  await dispatch<any>(createRules(ruleData))
                    .then(() => {
                      dispatch<any>(
                        setToast({
                          message: isEdit ? "Rule updated!" : `Rule created!`,
                          type: "success",
                          title: "Success",
                          onClose: () => {},
                        })
                      );
                      fetchRules();
                      reset();
                    })
                    .finally(() => setCreateLoader(false));
                  setOpenModal(false);
                  setIsEdit(false);
                }}
              >
                {createLoader ? (
                  <CircularProgress size={18} />
                ) : isEdit ? (
                  "Edit"
                ) : (
                  "Add"
                )}
              </Button>
            </div>
          </Box>
        </ModalWrapper>
      </Modal>

      {/** DATA TABLE */}
      <div>
        <CustomTable
          headings={[
            "SR NO.",
            "Type",
            "Condition",
            "Value",
            "Category",
            "Action",
          ]}
        >
          {listLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            data &&
            data?.map((rule: any, idx: number) => {
              return (
                <TableRow>
                  <TableCell
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      paddingLeft: "35px",
                    }}
                  >
                    {(pageNo - 1) * limit + idx + 1}
                  </TableCell>
                  <TableCell
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {rule?.type
                      ? rule.type.charAt(0).toUpperCase() + rule.type.slice(1)
                      : "-"}
                  </TableCell>

                  <TableCell
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {rule?.condition}
                  </TableCell>
                  <TableCell
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {(() => {
                      const value = rule?.value;

                      if (value == null || value === "") return "-"; // handle empty/null

                      const strValue = String(value).trim();

                      // If it already starts with "$", just show it as is
                      if (strValue.startsWith("$")) return strValue;

                      // If it's a number or starts with digits, prepend "$"
                      if (!isNaN(Number(strValue))) return `$${strValue}`;

                      // Otherwise treat as string and show as is
                      return strValue;
                    })()}
                  </TableCell>

                  <TableCell
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {rule?.category?.name?.length > 30
                      ? `${rule?.category?.name?.slice(0, 30)}...`
                      : rule?.category?.name}
                  </TableCell>
                  <TableCell
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      paddingLeft: 40,
                      cursor: "pointer",
                    }}
                  >
                    <Button
                      style={{
                        marginLeft: -30,
                      }}
                      onClick={async () => {
                        await dispatch<any>(getRuleByID(rule?.id)).then(
                          (res: any) => {
                            setRuleData({
                              rule_id: rule?.id,
                              type: res?.payload?.data?.type,
                              condition: res?.payload?.data?.condition,
                              category_id: res?.payload?.data?.category_id,
                              value: res?.payload?.data?.value,
                            });
                            setCategoryValue(
                              res?.payload?.data?.category?.name
                            );
                          }
                        );
                        setOpenModal(true);
                        setIsEdit(true);
                      }}
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      onClick={() => {
                        setDeleteModalOpen(true);
                        setRuleId(rule?.id);
                      }}
                      sx={{
                        ml: -3,
                      }}
                    >
                      <DeleteIconTable />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </CustomTable>

        {data?.length === 0 && (
          <div
            style={{
              textAlign: "center",
              marginTop: 20,
            }}
          >
            No rules found!
          </div>
        )}
        {/* Pagination */}
        {Math.ceil(totalRecords / limit) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
            <CommonPagination
              currentPage={pageNo}
              totalPages={Math.ceil(totalRecords / limit)}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </div>

      {/**DELETE RULE MODAL */}
      <Modal open={deleteModalOpen} onClose={handleDeleteCancel}>
        <ModalWrapper
          sx={{
            width: isMobile ? "350px" : "500px",
            p: 0,
            height: "370px",
            backgroundImage: "none",
            position: "relative",
            borderRadius: "20px",
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* Top right decoration */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <ModalTop />
          </div>
          {/* Bottom left decoration */}
          <div
            style={{
              position: "absolute",
              bottom: -6,
              left: 0,
              zIndex: 1,
            }}
          >
            <ModalBottom />
          </div>
          {/* Ensure close button is above decorations */}
          <CloseButtonWrapper
            sx={{ zIndex: 10, position: "relative", marginTop: 1 }}
          >
            <CloseButton onClick={handleDeleteCancel}>
              <CrossIcon />
            </CloseButton>
          </CloseButtonWrapper>
          <Box sx={{ p: 4, pt: 0 }}>
            {/* Trash icon with red bg */}
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}
            >
              <Box
                sx={{
                  background: "#F20F0014",
                  borderRadius: "50%",
                  p: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrashIconForPopup />
              </Box>
            </Box>
            <div
              style={{
                textAlign: "center",
                fontSize: isMobile ? "20px" : "24px",
                color: "#01060B",
                fontWeight: 700,
                marginTop: 0,
                marginBottom: 10,
              }}
            >
              Delete Rule
            </div>
            <div
              style={{
                width: isMobile ? "100%" : "250px",
                textAlign: "center",
                lineHeight: "24px",
                margin: "0 auto",
                fontSize: isMobile ? "12px" : "16px",
                color: "#6C6F71",
                fontWeight: 500,
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              Are you sure you want to delete the rule?
            </div>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 3,
                justifyContent: "center",
              }}
            >
              <Button
                sx={{
                  border: "2px solid #2977CC",
                  borderRadius: "8px",
                  width: "192px",
                  height: "51px",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#2977CC",
                }}
                onClick={handleDeleteCancel}
              >
                Close
              </Button>
              <Button
                sx={{
                  background: deleteLoader
                    ? "#ccc"
                    : "linear-gradient(to right, #2977CC, #1D538F)",
                  color: "white",
                  width: "192px",
                  height: "51px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
                onClick={async () => {
                  setDeleteLoader(true);
                  await dispatch<any>(deleteRule(ruleId))
                    .then(() => {
                      dispatch<any>(
                        setToast({
                          message: "Rule Deleted!",
                          type: "success",
                          title: "Success",
                          onClose: () => {},
                        })
                      );
                    })
                    .finally(() => setDeleteLoader(false));
                  setDeleteModalOpen(false);
                  fetchRules();
                }}
              >
                {deleteLoader ? <CircularProgress size={18} /> : "Yes"}
              </Button>
            </Box>
          </Box>
        </ModalWrapper>
      </Modal>
    </div>
  );
};

export default BookkeepingRules;
